import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Form,
  Alert,
  Spinner,
  Modal,
  ButtonGroup,
  Dropdown,
  OverlayTrigger,
  Tooltip,
  ProgressBar,
} from 'react-bootstrap';
import axios from 'axios';
import Icon from '../components/Icon';
import OperationProgress from '../components/OperationProgress';
import { clearBatchProgress, initBatchProgress, tickBatchProgress } from '../utils/batchProgress';
import {
  ValidationRules,
  validateForm,
  validateFleetReminderForm,
  hasErrors,
  formatErrors,
} from '../utils/validation';

const VALIDATION_SCAN_STEPS = [
  { endpoint: 'customers', label: 'Customers' },
  { endpoint: 'customer-sites', label: 'Customer Sites' },
  { endpoint: 'customer-contacts', label: 'Customer Contacts' },
  { endpoint: 'suppliers', label: 'Suppliers' },
  { endpoint: 'staff', label: 'Staff' },
  { endpoint: 'hire-category-groups', label: 'Hire Category Groups' },
  { endpoint: 'hire-categories', label: 'Hire Categories' },
  { endpoint: 'sales-categories', label: 'Sales Categories' },
  { endpoint: 'sales-items', label: 'Sales Items' },
  { endpoint: 'sales-items-individual', label: 'Sales Items Individual' },
  { endpoint: 'nominal-codes', label: 'Nominal Codes' },
  { endpoint: 'fleet-reminders', label: 'Fleet Reminders' },
  { endpoint: 'fleet-service-history', label: 'Fleet Service History' },
  { endpoint: 'meter-readings', label: 'Meter Readings' },
  { endpoint: 'live-contracts', label: 'Live Contracts' },
  { endpoint: 'customer-equipment', label: 'Customer Equipment' },
  { endpoint: 'inspection-tests', label: 'Inspection Tests' },
  { endpoint: 'inclusions', label: 'Inclusions' },
  { endpoint: 'hire-stock', label: 'Hire Stock (Individual)' },
  { endpoint: 'hire-stock-bulk', label: 'Hire Stock (Bulk)' },
  { endpoint: 'pricing-agreements', label: 'Pricing Agreements' },
  { endpoint: 'pricing-agreement-customer-sites', label: 'Pricing Agreement Customer Sites' },
  { endpoint: 'pricing-agreement-hire-rates', label: 'Pricing Agreement Hire Rates' },
  { endpoint: 'pricing-agreement-sales-prices', label: 'Pricing Agreement Sales Prices' },
  { endpoint: 'pricing-agreement-transport-rates', label: 'Pricing Agreement Transport Rates' },
];

const READ_ONLY_RECORD_FIELDS = new Set([
  'id',
  'user_id',
  'tenant_id',
  'created_at',
  'updated_at',
  'resolved_by',
  'resolved_by_username',
  'resolved_by_first_name',
  'resolved_by_last_name',
]);

const RECORD_API_ROUTES = {
  customers: '/api/customers',
  customer_sites: '/api/customer-sites',
  customer_contacts: '/api/customer-contacts',
  inclusions: '/api/inclusions',
  hire_categories: '/api/hire-categories',
  hire_category_groups: '/api/hire-category-groups',
  hire_stock_individual: '/api/hire-stock',
  hire_stock_bulk: '/api/hire-stock-bulk',
  sales_categories: '/api/sales-categories',
  sales_items: '/api/sales-items',
  sales_items_individual: '/api/sales-items-individual',
  suppliers: '/api/suppliers',
  staff: '/api/staff',
  fleet_reminders: '/api/fleet-reminders',
  fleet_service_history: '/api/fleet-service-history',
  meter_readings: '/api/meter-readings',
  live_contracts: '/api/live-contracts',
  customer_equipment: '/api/customer-equipment',
  inspection_tests: '/api/inspection-tests',
  nominal_codes: '/api/nominal-codes',
  pricing_agreements: '/api/pricing-agreements',
  pricing_agreement_customer_sites: '/api/pricing-agreement-customer-sites',
  pricing_agreement_hire_rates: '/api/pricing-agreement-hire-rates',
  pricing_agreement_sales_prices: '/api/pricing-agreement-sales-prices',
  pricing_agreement_transport_rates: '/api/pricing-agreement-transport-rates',
};

const TABLE_VALIDATION_RULES = {
  customers: ValidationRules.customer,
  customer_sites: ValidationRules.customerSite,
  customer_contacts: ValidationRules.customerContact,
  staff: ValidationRules.staff,
  suppliers: ValidationRules.supplier,
  hire_category_groups: ValidationRules.hireCategoryGroup,
  hire_categories: ValidationRules.hireCategory,
  sales_categories: ValidationRules.salesCategory,
  sales_items: ValidationRules.salesItem,
  sales_items_individual: ValidationRules.salesItemsIndividual,
  nominal_codes: ValidationRules.nominalCode,
  fleet_reminders: ValidationRules.fleetReminder,
  fleet_service_history: ValidationRules.fleetServiceHistory,
  meter_readings: ValidationRules.meterReading,
  live_contracts: ValidationRules.liveContract,
  customer_equipment: ValidationRules.customerEquipment,
  inspection_tests: ValidationRules.inspectionTest,
  inclusions: ValidationRules.inclusion,
  hire_stock_individual: ValidationRules.hireStock,
  hire_stock_bulk: ValidationRules.hireStockBulk,
};

function stripReadOnlyFields(record) {
  const payload = { ...record };
  READ_ONLY_RECORD_FIELDS.forEach((field) => {
    delete payload[field];
  });
  return payload;
}

function buildMergedUpdatePayload(recordData, fixValues, relatedFailures) {
  const payload = recordData ? stripReadOnlyFields(recordData) : {};

  for (const [fieldName, fieldValue] of Object.entries(fixValues)) {
    const cleanValue = typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue;
    if (cleanValue !== '' || relatedFailures.some((f) => f.field_name === fieldName)) {
      payload[fieldName] = cleanValue;
    }
  }

  return payload;
}

function formatDateForInput(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value).split('T')[0];
}

function getFieldInputConfig(tableName, fieldName) {
  const rules = TABLE_VALIDATION_RULES[tableName]?.[fieldName];
  if (rules?.type === 'date' || fieldName.endsWith('_date')) {
    return { inputType: 'date' };
  }
  if (!rules) {
    return { inputType: 'text' };
  }
  if (rules.type === 'integer') {
    return { inputType: 'number', step: '1' };
  }
  if (rules.type === 'number' || rules.type === 'money') {
    return { inputType: 'number', step: 'any' };
  }
  return { inputType: 'text' };
}

function validateRecordForm(tableName, payload) {
  if (tableName === 'fleet_reminders') {
    return validateFleetReminderForm(payload);
  }
  const rules = TABLE_VALIDATION_RULES[tableName];
  if (!rules) {
    return {};
  }
  return validateForm(payload, rules);
}

// Helper component to render a single field fix form
function FixFieldForm({
  failure,
  fixValues,
  setFixValues,
  getFieldOptions,
  isFirst,
  fieldName,
  tableName,
  currentValue,
  errorMessage,
  hasError = true,
  validationErrors = {},
  onFieldEdit,
  inputType = 'text',
  inputStep,
}) {
  const [fieldOptions, setFieldOptions] = useState({ type: 'text', options: [] });
  const [loading, setLoading] = useState(true);

  // Use provided values or fall back to failure object
  const actualFieldName = fieldName || failure?.field_name;
  const actualTableName = tableName || failure?.table_name;
  const actualCurrentValue = currentValue !== undefined ? currentValue : failure?.field_value;
  const actualErrorMessage = errorMessage || failure?.error_message;

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      const options = await getFieldOptions(actualTableName, actualFieldName);
      setFieldOptions(options);
      setLoading(false);
    };
    loadOptions();
  }, [actualTableName, actualFieldName]);

  const handleFieldChange = (value) => {
    const cleanValue = typeof value === 'string' ? value.trim() : value;
    setFixValues((prev) => ({
      ...prev,
      [actualFieldName]: cleanValue,
    }));
    if (onFieldEdit) {
      onFieldEdit(actualFieldName);
    }
  };

  const fieldValidationError = validationErrors[actualFieldName];
  const displayValue =
    inputType === 'date'
      ? formatDateForInput(fixValues[actualFieldName] ?? actualCurrentValue)
      : fixValues[actualFieldName] || '';

  const getFieldLabel = (fieldName) => {
    const labels = {
      parent_type: 'Parent Type',
      parent_code: 'Parent Code',
      incl_cat: 'Inclusion Category',
      incl_stockno: 'Inclusion Stock Number',
      sales_inclusion_method: 'Sales Inclusion Method',
    };
    return (
      labels[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  return (
    <div
      className={`border rounded p-3 mb-3`}
      style={{
        backgroundColor: hasError ? '#fff5f5' : '#f8f9fa',
        borderColor: hasError ? '#dc3545' : '#dee2e6',
      }}
    >
      <Row className="mb-2">
        <Col>
          <strong>Field:</strong> <code>{actualFieldName}</code>
          {hasError && (
            <Badge bg="danger" className="ms-2">
              Error
            </Badge>
          )}
        </Col>
      </Row>
      {hasError && actualErrorMessage && (
        <Row className="mb-2">
          <Col>
            <strong>Error:</strong>
            <div className="text-danger small mt-1">{actualErrorMessage}</div>
          </Col>
        </Row>
      )}
      <Form.Group className="mb-0">
        <Form.Label className="small">
          <strong>{getFieldLabel(actualFieldName)}:</strong>
        </Form.Label>
        {loading ? (
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        ) : fieldOptions.type === 'select' ? (
          (() => {
            const selectedValue = fixValues[actualFieldName] || actualCurrentValue || '';
            const selectOptions = [...fieldOptions.options];
            if (selectedValue && !selectOptions.some((opt) => opt.value === selectedValue)) {
              selectOptions.unshift({
                value: selectedValue,
                label: `${selectedValue} (not in list)`,
              });
            }

            return (
              <Form.Select
                value={fixValues[actualFieldName] || ''}
                onChange={(e) => handleFieldChange(e.target.value)}
                autoFocus={isFirst}
                isInvalid={!!fieldValidationError}
              >
                <option value="">-- Select a value --</option>
                {selectOptions.map((opt, idx) => (
                  <option key={idx} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            );
          })()
        ) : (
          <Form.Control
            type={inputType}
            step={inputStep}
            value={displayValue}
            onChange={(e) => handleFieldChange(e.target.value)}
            placeholder={inputType === 'date' ? undefined : 'Enter the correct value...'}
            autoFocus={isFirst}
            isInvalid={!!fieldValidationError}
          />
        )}
        {fieldValidationError && (
          <Form.Control.Feedback type="invalid">{fieldValidationError}</Form.Control.Feedback>
        )}
        <Form.Text className="text-muted">
          Current value: {actualCurrentValue || <em>(empty)</em>}
        </Form.Text>
      </Form.Group>
    </div>
  );
}

function ValidationFailures() {
  const [validationFailures, setValidationFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [tableFilter, setTableFilter] = useState('');
  const [summary, setSummary] = useState({
    totals: { total: 0, active: 0, resolved: 0 },
    byTable: [],
  });
  const [selectedFailures, setSelectedFailures] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentFailure, setCurrentFailure] = useState(null);
  const [notes, setNotes] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0, label: '' });
  const [showFixModal, setShowFixModal] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixAllSimilar, setFixAllSimilar] = useState(false);
  const [similarCount, setSimilarCount] = useState(0);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [relatedFailures, setRelatedFailures] = useState([]);
  const [fixValues, setFixValues] = useState({});
  const [fullRecordData, setFullRecordData] = useState(null);
  const [loadingFullRecord, setLoadingFullRecord] = useState(false);
  const [fixSaveError, setFixSaveError] = useState('');
  const [fixValidationErrors, setFixValidationErrors] = useState({});
  const [fixProgress, setFixProgress] = useState(null);

  useEffect(() => {
    fetchValidationFailures();
    fetchSummary();
  }, [pagination.page, statusFilter, tableFilter, searchTerm]);

  const fetchValidationFailures = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/validation-failures', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          status: statusFilter,
          table_name: tableFilter || undefined,
          search: searchTerm || undefined,
        },
      });
      setValidationFailures(response.data.validationFailures);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      console.error('Error fetching validation failures:', err);
      setError('Failed to load validation failures');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get('/api/validation-failures/summary');
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const handleResolve = async (id) => {
    try {
      await axios.put(`/api/validation-failures/${id}/resolve`);
      setSuccess('Validation failure marked as resolved');
      fetchValidationFailures();
      fetchSummary();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error resolving validation failure:', err);
      setError('Failed to resolve validation failure');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await axios.put(`/api/validation-failures/${id}/reactivate`);
      setSuccess('Validation failure reactivated');
      fetchValidationFailures();
      fetchSummary();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error reactivating validation failure:', err);
      setError('Failed to reactivate validation failure');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this validation failure record?')) {
      return;
    }

    try {
      await axios.delete(`/api/validation-failures/${id}`);
      setSuccess('Validation failure deleted');
      fetchValidationFailures();
      fetchSummary();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting validation failure:', err);
      setError('Failed to delete validation failure');
    }
  };

  const handleViewRecord = async (failure) => {
    // Map table names to their corresponding API endpoints
    const tableApiMap = {
      customers: `/api/customers/${failure.record_id}`,
      customer_sites: `/api/customer-sites/${failure.record_id}`,
      customer_contacts: `/api/customer-contacts/${failure.record_id}`,
      inclusions: `/api/inclusions/${failure.record_id}`,
      hire_categories: `/api/hire-categories/${failure.record_id}`,
      hire_category_groups: `/api/hire-category-groups/${failure.record_id}`,
      hire_stock_individual: `/api/hire-stock/${failure.record_id}`,
      hire_stock_bulk: `/api/hire-stock-bulk/${failure.record_id}`,
      sales_categories: `/api/sales-categories/${failure.record_id}`,
      sales_items: `/api/sales-items/${failure.record_id}`,
      suppliers: `/api/suppliers/${failure.record_id}`,
      staff: `/api/staff/${failure.record_id}`,
      fleet_reminders: `/api/fleet-reminders/${failure.record_id}`,
      inspection_tests: `/api/inspection-tests/${failure.record_id}`,
      nominal_codes: `/api/nominal-codes/${failure.record_id}`,
    };

    const apiEndpoint = tableApiMap[failure.table_name];
    if (!apiEndpoint) {
      setError(`No API endpoint found for table: ${failure.table_name}`);
      return;
    }

    try {
      setLoadingRecord(true);
      const response = await axios.get(apiEndpoint);
      setViewingRecord({
        ...failure,
        recordData: response.data,
      });
      setShowViewModal(true);
    } catch (err) {
      console.error('Error fetching record:', err);
      setError('Failed to load record details');
    } finally {
      setLoadingRecord(false);
    }
  };

  const handleBulkResolve = async () => {
    if (selectedFailures.length === 0) {
      setError('Please select at least one validation failure');
      return;
    }

    try {
      await axios.post('/api/validation-failures/bulk/resolve', { ids: selectedFailures });
      setSuccess(`${selectedFailures.length} validation failure(s) resolved`);
      setSelectedFailures([]);
      fetchValidationFailures();
      fetchSummary();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error bulk resolving:', err);
      setError('Failed to resolve validation failures');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFailures.length === 0) {
      setError('Please select at least one validation failure');
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedFailures.length} validation failure record(s)?`
      )
    ) {
      return;
    }

    try {
      await axios.post('/api/validation-failures/bulk/delete', { ids: selectedFailures });
      setSuccess(`${selectedFailures.length} validation failure(s) deleted`);
      setSelectedFailures([]);
      fetchValidationFailures();
      fetchSummary();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error bulk deleting:', err);
      setError('Failed to delete validation failures');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedFailures(validationFailures.map((vf) => vf.id));
    } else {
      setSelectedFailures([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedFailures.includes(id)) {
      setSelectedFailures(selectedFailures.filter((fid) => fid !== id));
    } else {
      setSelectedFailures([...selectedFailures, id]);
    }
  };

  const handleShowNotes = (failure) => {
    setCurrentFailure(failure);
    setNotes(failure.notes || '');
    setShowNotesModal(true);
  };

  const handleSaveNotes = async () => {
    try {
      await axios.put(`/api/validation-failures/${currentFailure.id}`, { notes });
      setSuccess('Notes updated successfully');
      setShowNotesModal(false);
      fetchValidationFailures();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Failed to save notes');
    }
  };

  // Get field options for dropdown fields
  const getFieldOptions = async (tableName, fieldName) => {
    try {
      // Define field-specific options for various tables
      const fieldConfig = {
        inclusions: {
          parent_type: [
            { value: 'C', label: 'C - Category' },
            { value: 'S', label: 'S - Stock' },
          ],
          sales_inclusion_method: [
            { value: 'A', label: 'A - Automatic' },
            { value: 'M', label: 'M - Manual' },
          ],
        },
        hire_categories: {
          vat_rate: [
            { value: '0', label: '0% - Zero Rated' },
            { value: '5', label: '5% - Reduced Rate' },
            { value: '20', label: '20% - Standard Rate' },
          ],
        },
        sales_categories: {
          vat_rate: [
            { value: '0', label: '0% - Zero Rated' },
            { value: '5', label: '5% - Reduced Rate' },
            { value: '20', label: '20% - Standard Rate' },
          ],
        },
      };

      // Check if this is a predefined dropdown
      if (fieldConfig[tableName]?.[fieldName]) {
        return { type: 'select', options: fieldConfig[tableName][fieldName] };
      }

      // Check for common foreign key fields that need dropdowns
      if (fieldName === 'parent_code' && tableName === 'inclusions') {
        // Fetch both hire and sales categories/stock
        const [hireCategories, salesCategories, hireStock, salesItems] = await Promise.all([
          axios.get('/api/hire-categories/codes'),
          axios.get('/api/sales-categories/codes'),
          axios.get('/api/hire-stock/stock-numbers'),
          axios.get('/api/sales-items/codes'),
        ]);

        const options = [
          ...hireCategories.data.map((c) => ({
            value: (c.code || '').toString().trim(),
            label: `${c.code} - ${c.name} (Hire Category)`,
          })),
          ...salesCategories.data.map((c) => ({
            value: (c.code || '').toString().trim(),
            label: `${c.code} - ${c.name} (Sales Category)`,
          })),
          ...hireStock.data.map((s) => ({
            value: (s.stock_no || '').toString().trim(),
            label: `${s.stock_no} - ${s.name} (Hire Stock)`,
          })),
          ...salesItems.data.map((s) => ({
            value: (s.stock_no || '').toString().trim(),
            label: `${s.stock_no} - ${s.name} (Sales Item)`,
          })),
        ];

        return { type: 'select', options };
      }

      if (fieldName === 'incl_cat' && tableName === 'inclusions') {
        const [hireCategories, salesCategories] = await Promise.all([
          axios.get('/api/hire-categories/codes'),
          axios.get('/api/sales-categories/codes'),
        ]);

        const options = [
          ...hireCategories.data.map((c) => ({
            value: (c.code || '').toString().trim(),
            label: `${c.code} - ${c.name} (Hire)`,
          })),
          ...salesCategories.data.map((c) => ({
            value: (c.code || '').toString().trim(),
            label: `${c.code} - ${c.name} (Sales)`,
          })),
        ];

        return { type: 'select', options };
      }

      if (fieldName === 'incl_stockno' && tableName === 'inclusions') {
        // Fetch all stock items - both hire and sales
        const [hireStockIndividual, hireStockBulk, salesItems] = await Promise.all([
          axios.get('/api/hire-stock'),
          axios.get('/api/hire-stock-bulk'),
          axios.get('/api/sales-items'),
        ]);

        const options = [
          ...hireStockIndividual.data.map((s) => ({
            value: (s.stock_no || '').toString().trim(),
            label: `${s.stock_no} - ${s.name} (Hire - ${s.cat})`,
            category: s.cat,
          })),
          ...hireStockBulk.data.map((s) => ({
            value: (s.stock_no || '').toString().trim(),
            label: `${s.stock_no} - ${s.name} (Hire Bulk - ${s.cat})`,
            category: s.cat,
          })),
          ...salesItems.data.map((s) => ({
            value: (s.stock_no || '').toString().trim(),
            label: `${s.stock_no} - ${s.name} (Sales - ${s.cat})`,
            category: s.cat,
          })),
        ];

        return { type: 'select', options };
      }

      if (
        (fieldName === 'group_name' || fieldName === 'group_code') &&
        tableName === 'hire_categories'
      ) {
        const response = await axios.get('/api/hire-category-groups');
        const options = response.data.map((g) => ({
          value: (g.group_name || '').toString().trim(),
          label: `${g.group_name}`,
        }));
        return { type: 'select', options };
      }

      // Category field for hire stock (individual and bulk)
      if (
        fieldName === 'cat' &&
        (tableName === 'hire_stock_individual' || tableName === 'hire_stock_bulk')
      ) {
        const response = await axios.get('/api/hire-categories/codes');
        const options = response.data.map((c) => ({
          value: (c.code || '').toString().trim(),
          label: `${c.code} - ${c.name}`,
        }));
        return { type: 'select', options };
      }

      if (fieldName === 'depot' && tableName === 'hire_stock_individual') {
        const response = await axios.get('/api/depots?limit=1000');
        const options = (response.data.depots || []).map((depot) => ({
          value: (depot.short_code || '').toString().trim(),
          label: `${depot.short_code} - ${depot.depot_name}`,
        }));
        return { type: 'select', options };
      }

      if (fieldName === 'nom_code' && tableName === 'hire_stock_individual') {
        const response = await axios.get('/api/nominal-codes', {
          params: { type: 'S', limit: 500 },
        });
        const options = (response.data.nominalCodes || []).map((nominal) => ({
          value: (nominal.code || '').toString().trim(),
          label: `${nominal.code} - ${nominal.name}`,
        }));
        return { type: 'select', options };
      }

      if (fieldName === 'provisional_type' && tableName === 'hire_stock_individual') {
        return {
          type: 'select',
          options: [
            { value: 'BORROWED', label: 'Borrowed' },
            { value: 'CROSS-HIRE', label: 'Cross Hire' },
            { value: 'LEASED', label: 'Leased' },
            { value: 'SALE-STOCK', label: 'Sale Stock' },
          ],
        };
      }

      // Category field for sales items
      if (fieldName === 'cat' && tableName === 'sales_items') {
        const response = await axios.get('/api/sales-categories/codes');
        const options = response.data.map((c) => ({
          value: (c.code || '').toString().trim(),
          label: `${c.code} - ${c.name}`,
        }));
        return { type: 'select', options };
      }

      // Hire category group field for hire categories
      if (fieldName === 'hire_category_group' && tableName === 'hire_categories') {
        const response = await axios.get('/api/hire-category-groups');
        const options = response.data.map((g) => ({
          value: (g.group_name || '').toString().trim(),
          label: `${g.group_name}`,
        }));
        return { type: 'select', options };
      }

      if (
        fieldName === 'account_no' &&
        (tableName === 'customer_sites' || tableName === 'customer_contacts')
      ) {
        const response = await axios.get('/api/customers', {
          params: { limit: 10000 },
        });
        const customers = response.data.customers || response.data;
        const options = (Array.isArray(customers) ? customers : []).map((c) => ({
          value: (c.account_no || '').toString().trim(),
          label: `${c.account_no} - ${c.customer_name}`,
        }));
        return { type: 'select', options };
      }

      // Default to text input
      return { type: 'text', options: [] };
    } catch (err) {
      console.error('Error getting field options:', err);
      return { type: 'text', options: [] };
    }
  };

  const handleShowFixModal = async (failure) => {
    setCurrentFailure(failure);
    setFixAllSimilar(false);
    setFullRecordData(null);
    setFixSaveError('');
    setFixValidationErrors({});
    setLoadingFullRecord(true);

    try {
      // Step 1: Fetch ALL validation failures from the database for this record
      const response = await axios.get('/api/validation-failures', {
        params: {
          table_name: failure.table_name,
          record_id: failure.record_id,
          status: 'active',
          limit: 100,
        },
      });

      let recordFailures = response.data.validationFailures || [];

      // Step 2: Fetch the actual record data
      const apiEndpoint = RECORD_API_ROUTES[failure.table_name];
      let recordData = null;

      if (apiEndpoint) {
        try {
          const recordResponse = await axios.get(`${apiEndpoint}/${failure.record_id}`);
          recordData = recordResponse.data;
        } catch (err) {
          console.error('Error fetching record data for validation:', err);
        }
      } else {
        console.warn('No API endpoint found for table:', failure.table_name);
      }

      setRelatedFailures(recordFailures);
      if (recordData) {
        setFullRecordData(recordData);
      }

      // Initialize fix values for all related failures (default behavior)
      const initialFixValues = {};
      for (const f of recordFailures) {
        initialFixValues[f.field_name] = (f.field_value || '').toString().trim();
      }

      // For inclusions, initialize all inclusion fields from the record
      if (failure.table_name === 'inclusions' && recordData) {
        const inclusionFields = [
          'parent_type',
          'parent_code',
          'incl_cat',
          'incl_stockno',
          'sales_inclusion_method',
        ];
        for (const field of inclusionFields) {
          if (recordData[field] !== undefined && recordData[field] !== null) {
            initialFixValues[field] = recordData[field].toString().trim();
          } else {
            initialFixValues[field] = '';
          }
        }
      }

      // For fleet reminders, show both scheduling fields when either has an error
      if (failure.table_name === 'fleet_reminders' && recordData) {
        const fleetDueFields = ['due_date', 'due_meter'];
        const hasDueFieldError = recordFailures.some((f) => fleetDueFields.includes(f.field_name));
        if (hasDueFieldError) {
          for (const field of fleetDueFields) {
            const value = recordData[field];
            initialFixValues[field] =
              value !== undefined && value !== null ? String(value).trim() : '';
          }
        }
      }

      setFixValues(initialFixValues);

      // Get field options for the primary failure (kept for backward compatibility)
      const fieldConfig = await getFieldOptions(failure.table_name, failure.field_name);
      setFieldOptions(fieldConfig);

      // Count how many similar failures exist across ALL records (for "Fix All Similar" feature)
      try {
        const similarResponse = await axios.get('/api/validation-failures', {
          params: {
            table_name: failure.table_name,
            status: 'active',
            limit: 1000, // Get enough to count similar
          },
        });

        const similarFailures = similarResponse.data.validationFailures.filter(
          (f) =>
            f.field_name === failure.field_name &&
            f.error_message === failure.error_message &&
            f.field_value === failure.field_value
        );
        setSimilarCount(similarFailures.length);
      } catch (err) {
        console.error('Error counting similar failures:', err);
        setSimilarCount(1);
      }
    } catch (err) {
      console.error('Error fetching validation failures for record:', err);
      setError('Failed to load validation failures for this record');
      setRelatedFailures([failure]); // Fallback to just the clicked failure
      setFixValues({ [failure.field_name]: (failure.field_value || '').toString().trim() });
      setSimilarCount(1);
    } finally {
      setLoadingFullRecord(false);
    }

    setShowFixModal(true);
  };

  const handleFix = async () => {
    if (!currentFailure) {
      setFixSaveError('No validation failure selected.');
      return;
    }

    const apiRoute = RECORD_API_ROUTES[currentFailure.table_name];
    if (!apiRoute) {
      setFixSaveError(`Cannot determine API route for table: ${currentFailure.table_name}`);
      return;
    }

    try {
      setFixing(true);
      setFixSaveError('');
      setFixValidationErrors({});

      if (fixAllSimilar) {
        const similarFailures = validationFailures.filter(
          (f) =>
            f.table_name === currentFailure.table_name &&
            f.field_name === currentFailure.field_name &&
            f.error_message === currentFailure.error_message &&
            f.field_value === currentFailure.field_value &&
            f.status === 'active'
        );

        const fieldName = currentFailure.field_name;
        const fieldValue =
          typeof fixValues[fieldName] === 'string'
            ? fixValues[fieldName].trim()
            : fixValues[fieldName];

        let successCount = 0;
        let failCount = 0;

        initBatchProgress(setFixProgress, {
          title: 'Fixing similar records...',
          total: similarFailures.length,
        });

        for (const failure of similarFailures) {
          try {
            const recordResponse = await axios.get(`${apiRoute}/${failure.record_id}`);
            const updatePayload = buildMergedUpdatePayload(
              recordResponse.data,
              { [fieldName]: fieldValue },
              [failure]
            );

            await axios.put(`${apiRoute}/${failure.record_id}`, updatePayload);
            await axios.put(`/api/validation-failures/${failure.id}/resolve`, {
              notes: `Bulk fixed by user: Changed ${fieldName} to "${fieldValue}"`,
            });
            successCount++;
            tickBatchProgress(setFixProgress, { success: true });
          } catch (err) {
            console.error(`Error fixing record ${failure.record_id}:`, err);
            failCount++;
            tickBatchProgress(setFixProgress, { failed: true });
          }
        }

        if (failCount > 0) {
          setFixSaveError(`${successCount} record(s) updated successfully. ${failCount} failed.`);
          return;
        }

        setShowFixModal(false);
        setSuccess(`${successCount} record(s) updated and validation failures resolved`);
        fetchValidationFailures();
        fetchSummary();
        setTimeout(() => setSuccess(''), 5000);
        return;
      }

      let recordData = fullRecordData;
      if (!recordData) {
        const recordResponse = await axios.get(`${apiRoute}/${currentFailure.record_id}`);
        recordData = recordResponse.data;
        setFullRecordData(recordData);
      }

      const updatePayload = buildMergedUpdatePayload(recordData, fixValues, relatedFailures);
      if (currentFailure.table_name === 'customer_sites' && updatePayload.account_no) {
        updatePayload.customer = updatePayload.account_no;
      }
      const errors = validateRecordForm(currentFailure.table_name, updatePayload);
      if (hasErrors(errors)) {
        setFixValidationErrors(errors);
        setFixSaveError(formatErrors(errors));
        return;
      }

      await axios.put(`${apiRoute}/${currentFailure.record_id}`, updatePayload);

      const dbFailures = relatedFailures.filter((f) => !f.isVirtual);
      if (dbFailures.length > 0) {
        initBatchProgress(setFixProgress, {
          title: 'Resolving validation failures...',
          total: dbFailures.length,
        });
      }

      for (const failure of dbFailures) {
        await axios.put(`/api/validation-failures/${failure.id}/resolve`, {
          notes: `Fixed by user: Updated ${Object.keys(fixValues).join(', ')}`,
        });
        tickBatchProgress(setFixProgress, { success: true });
      }

      setShowFixModal(false);
      setSuccess(`Record updated and ${dbFailures.length} validation failure(s) resolved`);
      fetchValidationFailures();
      fetchSummary();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error fixing validation failure:', err);
      setFixSaveError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to fix validation failure'
      );
    } finally {
      setFixing(false);
      clearBatchProgress(setFixProgress);
    }
  };

  const clearFixFieldError = (fieldName) => {
    setFixValidationErrors((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    setFixSaveError('');
  };

  const handleScanValidation = async () => {
    if (
      !window.confirm(
        'This will scan ALL records across Customers, Suppliers, Sites, Contacts, Stock, Categories, and other tables. Every field will be validated and lookup values checked. This may take a few moments. Continue?'
      )
    ) {
      return;
    }

    try {
      setScanning(true);
      setError('');
      setScanProgress({
        current: 0,
        total: VALIDATION_SCAN_STEPS.length,
        label: VALIDATION_SCAN_STEPS[0].label,
      });

      const totals = { scannedCount: 0, failureCount: 0, successCount: 0 };
      const stepErrors = [];

      for (let i = 0; i < VALIDATION_SCAN_STEPS.length; i++) {
        const step = VALIDATION_SCAN_STEPS[i];
        setScanProgress({
          current: i,
          total: VALIDATION_SCAN_STEPS.length,
          label: step.label,
        });

        try {
          const response = await axios.post(`/api/validation-scan/${step.endpoint}`);
          const data = response.data;
          if (data.error) {
            stepErrors.push(`${step.label}: ${data.error}`);
          } else {
            totals.scannedCount += data.scannedCount || 0;
            totals.failureCount += data.failureCount || 0;
            totals.successCount += data.successCount || 0;
          }
        } catch (stepErr) {
          console.error(`Error scanning ${step.label}:`, stepErr);
          stepErrors.push(step.label);
        }

        setScanProgress({
          current: i + 1,
          total: VALIDATION_SCAN_STEPS.length,
          label: step.label,
        });
      }

      setScanProgress({
        current: VALIDATION_SCAN_STEPS.length,
        total: VALIDATION_SCAN_STEPS.length,
        label: 'Refreshing results',
      });

      await Promise.all([fetchValidationFailures(), fetchSummary()]);

      if (stepErrors.length > 0) {
        setError(
          `Validation scan completed with errors in: ${stepErrors.join(', ')}. ${totals.scannedCount} records scanned, ${totals.failureCount} failures found.`
        );
      } else {
        setSuccess(
          `Validation scan completed: ${totals.scannedCount} records scanned, ${totals.failureCount} failures found across all tables`
        );
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Error scanning for validation failures:', err);
      setError('Failed to scan for validation failures');
    } finally {
      setScanning(false);
      setScanProgress({ current: 0, total: 0, label: '' });
    }
  };

  const getTableDisplayName = (tableName) => {
    const tableNames = {
      customers: 'Customers',
      customer_sites: 'Customer Sites',
      customer_contacts: 'Customer Contacts',
      staff: 'Staff',
      suppliers: 'Suppliers',
      hire_categories: 'Hire Categories',
      hire_category_groups: 'Hire Category Groups',
      hire_stock: 'Hire Stock',
      hire_stock_individual: 'Hire Stock (Individual)',
      hire_stock_bulk: 'Hire Stock (Bulk)',
      sales_categories: 'Sales Categories',
      sales_items: 'Sales Items',
      sales_items_individual: 'Sales Items Individual',
      fleet_reminders: 'Fleet Reminders',
      fleet_service_history: 'Fleet Service History',
      meter_readings: 'Meter Readings',
      live_contracts: 'Live Contracts',
      customer_equipment: 'Customer Equipment',
      inspection_tests: 'Inspection Tests',
      inclusions: 'Inclusions',
      nominal_codes: 'Nominal Codes',
      pricing_agreements: 'Pricing Agreements',
      pricing_agreement_customer_sites: 'Pricing Agreement Customer Sites',
      pricing_agreement_hire_rates: 'Pricing Agreement Hire Rates',
      pricing_agreement_sales_prices: 'Pricing Agreement Sales Prices',
      pricing_agreement_transport_rates: 'Pricing Agreement Transport Rates',
    };
    return tableNames[tableName] || tableName;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const uniqueTables = [...new Set(summary.byTable.map((item) => item.table_name))].sort();

  const scanProgressPercent =
    scanProgress.total > 0 ? Math.round((scanProgress.current / scanProgress.total) * 100) : 0;

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <Icon name="clipboard" className="me-2" />
                Validation Failures
              </h2>
              <p className="text-muted mb-0">
                Manage and resolve data validation issues across all records
              </p>
            </div>
            <Button variant="primary" onClick={handleScanValidation} disabled={scanning}>
              {scanning ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Scanning...
                </>
              ) : (
                <>
                  <Icon name="search" className="me-2" />
                  Scan for Validation Failures
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <div className="card border-primary">
            <div className="card-body">
              <h5 className="card-title text-primary">
                <Icon name="clipboard" className="me-2" />
                Total Issues
              </h5>
              <h2 className="mb-0">{summary.totals.total || 0}</h2>
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="card border-danger">
            <div className="card-body">
              <h5 className="card-title text-danger">
                <Icon name="exclamationTriangle" className="me-2" />
                Active Issues
              </h5>
              <h2 className="mb-0">{summary.totals.active || 0}</h2>
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="card border-success">
            <div className="card-body">
              <h5 className="card-title text-success">
                <Icon name="checkCircle" className="me-2" />
                Resolved
              </h5>
              <h2 className="mb-0">{summary.totals.resolved || 0}</h2>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <Icon name="exclamationTriangle" className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          <Icon name="checkCircle" className="me-2" />
          {success}
        </Alert>
      )}

      {scanning && (
        <Alert variant="info" className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>
              <Spinner animation="border" size="sm" className="me-2" />
              {scanProgress.label === 'Refreshing results'
                ? 'Refreshing validation failures grid...'
                : `Scanning ${scanProgress.label} (${Math.min(scanProgress.current + 1, scanProgress.total)} of ${scanProgress.total})`}
            </strong>
            <span>{scanProgressPercent}%</span>
          </div>
          <ProgressBar
            now={scanProgressPercent}
            animated
            striped
            label={`${scanProgressPercent}%`}
            variant="info"
          />
          <Form.Text className="text-muted d-block mt-2">
            Validating all fields and lookup values. The grid will refresh when the scan finishes.
          </Form.Text>
        </Alert>
      )}

      {loading && !scanning && (
        <ProgressBar animated now={100} className="mb-3" variant="primary" />
      )}

      {/* Filters and Search */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={statusFilter}
              disabled={scanning}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
            >
              <option value="active">Active Only</option>
              <option value="resolved">Resolved Only</option>
              <option value="all">All</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Table</Form.Label>
            <Form.Select
              value={tableFilter}
              disabled={scanning}
              onChange={(e) => {
                setTableFilter(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
            >
              <option value="">All Tables</option>
              {uniqueTables.map((table) => (
                <option key={table} value={table}>
                  {getTableDisplayName(table)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Search</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by error message, field name, or value..."
              value={searchTerm}
              disabled={scanning}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Bulk Actions */}
      {selectedFailures.length > 0 && (
        <Row className="mb-3">
          <Col>
            <Alert variant="info">
              <Icon name="checkCircle" className="me-2" />
              {selectedFailures.length} item(s) selected
              <ButtonGroup className="ms-3">
                <Button variant="success" size="sm" onClick={handleBulkResolve}>
                  <Icon name="checkCircle" className="me-1" />
                  Resolve Selected
                </Button>
                <Button variant="danger" size="sm" onClick={handleBulkDelete}>
                  <Icon name="trash" className="me-1" />
                  Delete Selected
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setSelectedFailures([])}>
                  Clear Selection
                </Button>
              </ButtonGroup>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Validation Failures Table */}
      <div className="position-relative" style={{ minHeight: scanning ? '240px' : undefined }}>
        {scanning && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ zIndex: 2, backgroundColor: 'rgba(255, 255, 255, 0.65)' }}
          >
            <div className="text-center px-4" style={{ maxWidth: '420px' }}>
              <Spinner animation="border" role="status" className="mb-3" />
              <div className="fw-semibold mb-2">
                Scanning {scanProgress.label} ({scanProgressPercent}%)
              </div>
              <ProgressBar now={scanProgressPercent} animated striped className="mb-0" />
            </div>
          </div>
        )}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : validationFailures.length === 0 ? (
          <Alert variant="info">
            <Icon name="info" className="me-2" />
            No validation failures found.{' '}
            {statusFilter === 'active' ? 'All data is valid!' : 'Try adjusting your filters.'}
          </Alert>
        ) : (
          <>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedFailures.length === validationFailures.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th style={{ width: '110px' }}>Table</th>
                    <th style={{ width: '110px' }}>Field</th>
                    <th style={{ width: '120px' }}>Current Value</th>
                    <th style={{ width: '300px' }}>Error Message</th>
                    <th style={{ width: '95px' }}>Created</th>
                    <th style={{ width: '180px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {validationFailures.map((failure) => (
                    <tr key={failure.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedFailures.includes(failure.id)}
                          onChange={() => handleSelectOne(failure.id)}
                        />
                      </td>
                      <td>
                        <Badge bg="secondary">{getTableDisplayName(failure.table_name)}</Badge>
                      </td>
                      <td>
                        <code>{failure.field_name}</code>
                      </td>
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{failure.field_value || '(empty)'}</Tooltip>}
                        >
                          <span
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: '110px' }}
                          >
                            {failure.field_value || <em className="text-muted">(empty)</em>}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td
                        className="text-danger"
                        style={{ fontSize: '0.9em', whiteSpace: 'normal', wordBreak: 'break-word' }}
                      >
                        {failure.error_message}
                      </td>
                      <td className="text-nowrap small">{formatDate(failure.created_at)}</td>
                      <td>
                        <ButtonGroup size="sm">
                          <OverlayTrigger placement="top" overlay={<Tooltip>View Record</Tooltip>}>
                            <Button variant="info" onClick={() => handleViewRecord(failure)}>
                              <Icon name="eye" />
                            </Button>
                          </OverlayTrigger>
                          {failure.status === 'active' && (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Fix & Retry</Tooltip>}
                            >
                              <Button variant="primary" onClick={() => handleShowFixModal(failure)}>
                                <Icon name="pencil" />
                              </Button>
                            </OverlayTrigger>
                          )}
                          {failure.status === 'active' ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Mark as Resolved</Tooltip>}
                            >
                              <Button variant="success" onClick={() => handleResolve(failure.id)}>
                                <Icon name="checkCircle" />
                              </Button>
                            </OverlayTrigger>
                          ) : (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Reactivate</Tooltip>}>
                              <Button
                                variant="warning"
                                onClick={() => handleReactivate(failure.id)}
                              >
                                <Icon name="exclamationTriangle" />
                              </Button>
                            </OverlayTrigger>
                          )}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View/Edit Notes</Tooltip>}
                          >
                            <Button variant="info" onClick={() => handleShowNotes(failure)}>
                              <Icon name="documentText" />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                            <Button variant="danger" onClick={() => handleDelete(failure.id)}>
                              <Icon name="trash" />
                            </Button>
                          </OverlayTrigger>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Row className="mt-3">
                <Col className="d-flex justify-content-between align-items-center">
                  <div>
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} validation failures
                  </div>
                  <ButtonGroup>
                    <Button
                      variant="outline-primary"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    >
                      <Icon name="chevronLeft" /> Previous
                    </Button>
                    <Button variant="outline-primary" disabled>
                      Page {pagination.page} of {pagination.totalPages}
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    >
                      Next <Icon name="chevronRight" />
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>
            )}
          </>
        )}
      </div>

      {/* Notes Modal */}
      <Modal show={showNotesModal} onHide={() => setShowNotesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon name="documentText" className="me-2" />
            Validation Failure Notes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentFailure && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Table:</strong> {getTableDisplayName(currentFailure.table_name)}
                </Col>
                <Col md={6}>
                  <strong>Record ID:</strong> {currentFailure.record_id}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Field:</strong> <code>{currentFailure.field_name}</code>
                </Col>
                <Col md={6}>
                  <strong>Status:</strong>{' '}
                  {currentFailure.status === 'active' ? (
                    <Badge bg="danger">Active</Badge>
                  ) : (
                    <Badge bg="success">Resolved</Badge>
                  )}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Error Message:</strong>
                  <div className="text-danger mt-1">{currentFailure.error_message}</div>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Current Value:</strong>
                  <div className="mt-1">
                    {currentFailure.field_value || <em className="text-muted">(empty)</em>}
                  </div>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>
                  <strong>Notes:</strong>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this validation failure..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveNotes}>
            <Icon name="save" className="me-1" />
            Save Notes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Fix Modal */}
      <Modal show={showFixModal} onHide={() => setShowFixModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon name="pencil" className="me-2" />
            Fix Validation Failure
            {relatedFailures.length > 1 ? `s (${relatedFailures.length} Errors)` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fixProgress && (
            <OperationProgress
              title={fixProgress.title}
              processed={fixProgress.processed}
              total={fixProgress.total}
              success={fixProgress.success}
              failed={fixProgress.failed}
              variant="primary"
              className="mb-3"
            />
          )}
          {currentFailure && (
            <>
              {fixSaveError && (
                <Alert variant="danger" dismissible onClose={() => setFixSaveError('')}>
                  <Icon name="exclamationTriangle" className="me-2" />
                  {fixSaveError}
                </Alert>
              )}

              {relatedFailures.length > 1 && (
                <Alert variant="warning">
                  <Icon name="exclamationTriangle" className="me-2" />
                  <strong>This record has {relatedFailures.length} validation errors!</strong>
                  <div className="mt-2">
                    All {relatedFailures.length} failing fields are shown below (including errors
                    detected in real-time). You must fix all of them to save the record
                    successfully.
                  </div>
                </Alert>
              )}

              <Alert variant="info">
                <Icon name="info" className="me-2" />
                {currentFailure.table_name === 'inclusions' ? (
                  <>
                    All inclusion fields are shown below.{' '}
                    {relatedFailures.length > 0 && (
                      <>
                        <strong>
                          {relatedFailures.length} field
                          {relatedFailures.length > 1 ? 's have' : ' has'} validation errors.
                        </strong>{' '}
                      </>
                    )}
                    Update any fields as needed to fix the issues. The record will be updated and
                    all validation failures will be automatically resolved.
                  </>
                ) : relatedFailures.length > 1 ? (
                  <>
                    Update all fields below to fix all validation issues. The record will be updated
                    and all validation failures will be automatically resolved.
                  </>
                ) : (
                  <>
                    Update the field value below to fix the validation issue. The record will be
                    updated and the validation failure will be automatically resolved.
                  </>
                )}
              </Alert>

              {similarCount > 1 && (
                <Alert variant="warning">
                  <Icon name="exclamationTriangle" className="me-2" />
                  <strong>{similarCount} similar failures found!</strong>
                  <div className="mt-2">
                    Multiple records have the same error for{' '}
                    <code>{currentFailure.field_name}</code>. You can fix all of them at once or
                    just this record.
                  </div>
                </Alert>
              )}

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Table:</strong> {getTableDisplayName(currentFailure.table_name)}
                </Col>
                <Col md={6}>
                  <strong>Record ID:</strong> {currentFailure.record_id}
                  {relatedFailures.length > 1 && (
                    <Badge bg="danger" className="ms-2">
                      {relatedFailures.length} errors
                    </Badge>
                  )}
                </Col>
              </Row>

              <hr />

              {loadingFullRecord ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading record...</span>
                  </Spinner>
                </div>
              ) : currentFailure.table_name === 'inclusions' && fullRecordData ? (
                <>
                  <h6 className="mb-3">
                    <Icon name="pencil" className="me-2" />
                    Edit All Inclusion Fields
                  </h6>
                  {[
                    'parent_type',
                    'parent_code',
                    'incl_cat',
                    'incl_stockno',
                    'sales_inclusion_method',
                  ].map((fieldName, idx) => {
                    const failureForField = relatedFailures.find((f) => f.field_name === fieldName);
                    const inputConfig = getFieldInputConfig('inclusions', fieldName);
                    return (
                      <FixFieldForm
                        key={fieldName}
                        failure={failureForField}
                        fieldName={fieldName}
                        tableName="inclusions"
                        currentValue={fullRecordData[fieldName]}
                        errorMessage={failureForField?.error_message}
                        hasError={!!failureForField}
                        fixValues={fixValues}
                        setFixValues={setFixValues}
                        getFieldOptions={getFieldOptions}
                        isFirst={idx === 0}
                        validationErrors={fixValidationErrors}
                        onFieldEdit={clearFixFieldError}
                        inputType={inputConfig.inputType}
                        inputStep={inputConfig.step}
                      />
                    );
                  })}
                </>
              ) : currentFailure.table_name === 'fleet_reminders' &&
                fullRecordData &&
                relatedFailures.some((f) => ['due_date', 'due_meter'].includes(f.field_name)) ? (
                <>
                  <h6 className="mb-3">
                    <Icon name="pencil" className="me-2" />
                    Fix Scheduling — Due Date or Due Meter Required
                  </h6>
                  <Alert variant="light" className="mb-3 small">
                    Enter either a due date or a due meter reading. You do not need both.
                  </Alert>
                  {['due_date', 'due_meter'].map((fieldName, idx) => {
                    const failureForField = relatedFailures.find((f) => f.field_name === fieldName);
                    const inputConfig = getFieldInputConfig('fleet_reminders', fieldName);
                    return (
                      <FixFieldForm
                        key={fieldName}
                        failure={failureForField}
                        fieldName={fieldName}
                        tableName="fleet_reminders"
                        currentValue={fullRecordData[fieldName]}
                        errorMessage={failureForField?.error_message}
                        hasError={!!failureForField}
                        fixValues={fixValues}
                        setFixValues={setFixValues}
                        getFieldOptions={getFieldOptions}
                        isFirst={idx === 0}
                        validationErrors={fixValidationErrors}
                        onFieldEdit={clearFixFieldError}
                        inputType={inputConfig.inputType}
                        inputStep={inputConfig.step}
                      />
                    );
                  })}
                  {relatedFailures
                    .filter((f) => !['due_date', 'due_meter'].includes(f.field_name))
                    .map((failure, idx) => {
                      const inputConfig = getFieldInputConfig(
                        currentFailure.table_name,
                        failure.field_name
                      );
                      return (
                        <FixFieldForm
                          key={failure.id}
                          failure={failure}
                          fixValues={fixValues}
                          setFixValues={setFixValues}
                          getFieldOptions={getFieldOptions}
                          isFirst={
                            idx === 0 &&
                            !relatedFailures.some((f) =>
                              ['due_date', 'due_meter'].includes(f.field_name)
                            )
                          }
                          validationErrors={fixValidationErrors}
                          onFieldEdit={clearFixFieldError}
                          inputType={inputConfig.inputType}
                          inputStep={inputConfig.step}
                        />
                      );
                    })}
                </>
              ) : relatedFailures.length > 0 ? (
                <>
                  <h6
                    className="mb-3"
                    style={{ color: relatedFailures.length > 1 ? '#dc3545' : '#0d6efd' }}
                  >
                    <Icon
                      name={relatedFailures.length > 1 ? 'exclamationTriangle' : 'pencil'}
                      className="me-2"
                    />
                    {relatedFailures.length > 1
                      ? `Fix All ${relatedFailures.length} Validation Errors`
                      : 'Fix Validation Error'}
                  </h6>

                  {relatedFailures.map((failure, idx) => {
                    const inputConfig = getFieldInputConfig(
                      currentFailure.table_name,
                      failure.field_name
                    );
                    return (
                      <FixFieldForm
                        key={failure.id}
                        failure={failure}
                        fixValues={fixValues}
                        setFixValues={setFixValues}
                        getFieldOptions={getFieldOptions}
                        isFirst={idx === 0}
                        validationErrors={fixValidationErrors}
                        onFieldEdit={clearFixFieldError}
                        inputType={inputConfig.inputType}
                        inputStep={inputConfig.step}
                      />
                    );
                  })}
                </>
              ) : (
                <Alert variant="warning">
                  <Icon name="exclamationTriangle" className="me-2" />
                  No validation failures found for this record.
                </Alert>
              )}

              {similarCount > 1 && (
                <>
                  <hr className="my-3" />
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="fixAllSimilar"
                      label={`Apply fix for "${currentFailure.field_name}" to all ${similarCount} similar failures across different records`}
                      checked={fixAllSimilar}
                      onChange={(e) => setFixAllSimilar(e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      This will update the <code>{currentFailure.field_name}</code> field on all
                      records with the same error and current value.
                    </Form.Text>
                  </Form.Group>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFixModal(false)} disabled={fixing}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleFix} disabled={fixing}>
            {fixing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {fixAllSimilar ? `Fixing ${similarCount} records...` : 'Fixing...'}
              </>
            ) : (
              <>
                <Icon name="checkCircle" className="me-1" />
                {fixAllSimilar
                  ? `Fix All ${similarCount} Similar Records`
                  : `Fix & Resolve${relatedFailures.length > 1 ? ` All ${relatedFailures.length}` : ''}`}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Record Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon name="eye" className="me-2" />
            View Record - {viewingRecord && getTableDisplayName(viewingRecord.table_name)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingRecord ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : viewingRecord && viewingRecord.recordData ? (
            <>
              <Alert variant="warning" className="mb-3">
                <Icon name="exclamationTriangle" className="me-2" />
                <strong>Validation Error:</strong> {viewingRecord.error_message}
              </Alert>

              <div className="mb-3">
                <strong>Field with Error:</strong>{' '}
                <Badge bg="danger">{viewingRecord.field_name}</Badge>{' '}
                <strong>Current Value:</strong>{' '}
                <code>{viewingRecord.field_value || '(empty)'}</code>
              </div>

              <hr />

              <h6 className="mb-3">Record Details (Read-Only)</h6>
              <div
                className="border rounded p-3"
                style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}
              >
                <Table size="sm" bordered>
                  <tbody>
                    {Object.entries(viewingRecord.recordData).map(([key, value]) => {
                      // Skip internal fields
                      if (['user_id', 'created_at', 'updated_at'].includes(key)) {
                        return null;
                      }

                      // Highlight the field with the error
                      const isErrorField = key === viewingRecord.field_name;

                      return (
                        <tr key={key} className={isErrorField ? 'table-danger' : ''}>
                          <td style={{ width: '30%' }}>
                            <strong>
                              {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                            </strong>
                            {isErrorField && (
                              <Badge bg="danger" className="ms-2">
                                Error Field
                              </Badge>
                            )}
                          </td>
                          <td>
                            {value === null || value === undefined || value === '' ? (
                              <em className="text-muted">(empty)</em>
                            ) : typeof value === 'boolean' ? (
                              <Badge bg={value ? 'success' : 'secondary'}>
                                {value ? 'Yes' : 'No'}
                              </Badge>
                            ) : (
                              <code>{String(value)}</code>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <Alert variant="info">No record data available</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ValidationFailures;
